/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the content of 'Sent Documents' or 'Documents envoyés' menu of the sidebar
 * It shows a table containing documents that has been sent by the authenticated user
 */

// Imports
import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';

// Interfaces
import { PublicCabinet } from '../../interfaces/ICabinets';
import { AlertType, SortOrder, SortType, TableRow } from '../../interfaces/IUtils';
import { Dossier } from '../../interfaces/IDossiers';

// Utils
import { dateToDDMMYYYY, getDossierNameFormated } from '../utils/Format';

// Buroclic adaptive components
import { BCTable } from '../buroclic/BCTable';
import { ApiCallConstructor, HTTPResponse } from '../utils/ApiCallConstructor';
import { ErrorForUsr, displayAlert, signOut } from '../utils/Errors';
import { Action } from '../../interfaces/IActions';
import { toast } from 'react-toastify';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { printElement } from '../utils/Miscellaneous';


export const EventsTable = (props) => {
    const navigate = useNavigate();
    const cabinet = (JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet);
    const dossier: Dossier = props.dossier;

    // Sent documents history
    const [actions, setActions] = useState<Action[]>([] as Action[]);

    // Get initial data
    useEffect(() => {
        // Get actions attached to the current folder/dossier
        const fetchActions = async () => {
            try {
                const getActionsResponse: HTTPResponse = await toast.promise(
                    ApiCallConstructor({
                        endpoint: `/me/dossiers/${dossier.DOS_REF}/actions`,
                        method: 'GET'
                    }),
                    {
                        pending: `Récupération des évènements en cours`,
                        error: { render({ data }) { return `Erreur lors de la récupération des évènements : ${(data as HTTPResponse).error}` } }
                    }
                );

                // Store documents
                setActions(getActionsResponse.result as Action[]);
            } catch (err: any) {
                // Handle redirection for session expiration
                if (err.error === ErrorForUsr.NotSignedIn)
                    signOut((cabAlias) => navigate(`/${cabAlias}/login`));

                console.error(err.error)
                throw err;
            }
        }
        fetchActions().catch((error :any) => displayAlert( { message: error.error, type: AlertType.error } ));
    }, [navigate, dossier]);


    // Deposit History Table's settings
    const [tableRows, setTableRows] = useState<TableRow[]>([]);
    useEffect(() => {
        const tmpRows: TableRow[] = [];
        actions.forEach(action => {
            tmpRows.push({
                'id': action.ACT_ID,
                'Date': action.ACT_DATE,
                'Lieu': action.ACT_LIEU ?? '',
                'Descriptif': action.ACT_LIBELLE,
                'Intervenant': action.ACT_ACTEUR,
                'Commentaire': action.ACT_DESCRIPTION ?? ''
            });
        });
        setTableRows(tmpRows);
    }, [actions]);

    // JSX Template
    return (
        <div className={`flex flex-col my-3 mx-4 overflow-auto ${props.className ? props.className : ''}`}>

            {/* Actions */}
            <div className="flex flex-col flex-grow mt-2">
                {/* Title */}
                <div className="flex flex-row h-8 mb-4 place-items-center">
                    <div className="font-bold my-2 grow">
                        <span className='text-md lg:text-lg'><FontAwesomeIcon className={`w-6 h-6 lg:w-8 lg:h-8`} icon={faClock} style={{ color: cabinet.color + 'CF' }} /> Evènements</span>
                        <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
                    </div>
                </div>

                {/* Actions's table */}
                <BCTable id="actions-table-dos" key={'actions-' + tableRows.length} sortSettings={{ colName: 'Date', order: SortOrder.desc }} searchbar={true} className={'table-auto mt-2'}
                    buttons={[{ name: 'Imprimer', icon: faPrint, action: (selectedRows: TableRow[]) => { printElement(document.getElementById('actions-table-dos'), { pageTitle: `Nouveaux évènements (${getDossierNameFormated(dossier)})`, logoBase64: cabinet.logo, orientation: 'landscape' }); }, withoutSelection: true }]}
                    columns={[
                        { 'name': 'id', show: false }, // ID
                        { 'name': 'Date', show: true, className: 'text-left w-[150px]', formatBeforeRender: (row: TableRow, colKey: string) => dateToDDMMYYYY(row[colKey], true), sort: SortType.Date, usedForResearch: true },
                        { 'name': 'Lieu', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                        { 'name': 'Descriptif', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                        { 'name': 'Intervenant', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                        { 'name': 'Commentaire', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                    ]} data={tableRows} />
            </div>
        </div>
    );
};

EventsTable.propTypes = {
    dossier: PropTypes.object.isRequired,
    className: PropTypes.string
}